import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ONLY } from "./_shared.js";

/**
 * `SHARE-TRANSPARENCY` (@UC0212) — trang **redirect**: trợ lý dẫn chủ nhóm tới màn "Sổ công khai" (`/groups/:groupId/public-link`)
 * để tạo link XEM SỔ không đăng nhập rồi gửi qua Zalo. 0 bút toán, 0 executor.
 *
 * 📜 Lịch sử: QĐ PO 22/09 giữ `draft` vì mobile chưa có màn nào gọi `POST /groups/:id/transparency-links`, và dặn *"khi mobile
 * dựng màn, quyết T1-hay-redirect THEO MÀN ĐÃ CÓ"*. QĐ PO Q2 28/09 (link sổ công khai vào MVP) ⇒ US-AIV2-0112 H11 dựng màn đó.
 *
 * 🔴 **CHỌN `redirect`, KHÔNG T1 — đọc trước khi "cho trợ lý tạo link luôn":** T1 phải in URL (chứa token) vào câu `done` ⇒ token
 * — chìa khoá của một trang công khai — nằm VĨNH VIỄN trong `ai_messages` (append-only) và đi vào ngữ cảnh LLM các lượt sau
 * (`AI_RECENT_TURNS_WINDOW`). Ngược đúng nguyên tắc hash-only của link (0813: *"server không đọc ngược được URL của link còn
 * sống"*). `redirect` giữ token CHỈ trong RAM màn app, và dùng CHUNG một cơ chế với mục ở Cài đặt nhóm. Cái mất: một chạm.
 * 🔁 Xem lại khi: có cách trả URL cho client NGOÀI tin nhắn lưu (thẻ một-lần không ghi `ai_messages`).
 *
 * Vai = CHỈ chủ nhóm, copy ĐÚNG `@RequireGroupRoles(OWNER)` của cả ba cửa màn gọi (`transparency.controller.ts`) — mời rộng hơn
 * là dẫn người ta vào màn 403 (CLAUDE.md §4). Số hiện trên trang công khai do SERVER quyết: scope link ∩ cấu hình nhóm
 * (`effectivePublicScope` — `transparency_mode` gác tiền, `payment_status_visibility` gác tên; H11 S01), trang này không hứa con số.
 */
export const SHARE_TRANSPARENCY = {
  id: "SHARE-TRANSPARENCY",
  uc: "@UC0212",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ONLY,
  flags: [],
  slots: [],
  llm: {
    description: "Tạo liên kết trang sổ minh bạch (thu/chi, số dư theo cấu hình nhóm) để gửi cho cả nhóm xem.",
    examples: [
      "chia sẻ sổ minh bạch cho cả nhóm",
      "chia se so minh bach",
      "tạo link công khai thu chi của quỹ",
      "tao link cong khai thu chi",
      "cho phụ huynh xem sổ quỹ",
      "cho phu huynh xem so quy",
      "share sổ quỹ",
      "lấy link minh bạch",
      "gửi bảng thu chi cho mọi người xem",
      "link công khai",
      "tạo link xem sổ",
      "share link quỹ",
      // ——— 0105: thêm cho đủ 40 ———
      "share link quy",
      "tao link xem so",
      "link cong khai",
      "lay link minh bach",
      "share so quy",
      "gui bang thu chi cho moi nguoi xem",
      "tạo link minh bạch cho nhóm",
      "tao link minh bach cho nhom",
      "làm link để cả nhóm xem sổ quỹ",
      "lam link de ca nhom xem so quy",
      "công khai sổ quỹ cho mọi người",
      "cong khai so quy cho moi nguoi",
      "tạo trang minh bạch của quỹ",
      "tao trang minh bach cua quy",
      "cho cả lớp xem thu chi của quỹ",
      "cho ca lop xem thu chi cua quy",
      "mình muốn minh bạch sổ quỹ với nhóm",
      "minh muon minh bach so quy voi nhom",
      "tạo đường dẫn công khai cho sổ quỹ",
      "tao duong dan cong khai cho so quy",
      "share sổ minh bạch lên nhóm chat",
      "share so minh bach len nhom chat",
      "tạo link để ai cũng xem được thu chi",
      "tao link de ai cung xem duoc thu chi",
      "công khai thu chi nhóm",
      "cong khai thu chi nhom",
      "link minh bạch quỹ lớp",
      "link minh bach quy lop",
      // ——— US-AIV2-0112 H11: câu người dùng thật (probe 28/09 A15 0/2) — "không cần cài app" là lý do phụ huynh cần link ———
      "cho phụ huynh xem sổ mà không cần cài app",
      "cho phu huynh xem so ma khong can cai app",
    ],
  },
  screen: "/groups/:groupId/public-link",
  screenShows: "public_link",
  /** `@UC0216` — ba cửa mobile màn "Sổ công khai" gọi (`groups_endpoints.dart` `publicLedgerLinks` · `publicLedgerLink`). */
  endpoints: [
    "POST /groups/{groupId}/transparency-links",
    "GET /groups/{groupId}/transparency-links",
    "DELETE /groups/{groupId}/transparency-links/{lid}",
  ],
  facts: [],
  templates: {
    redirect:
      "Mình mở màn Sổ công khai để bạn tạo link xem sổ và gửi vào nhóm Zalo nhé. Link chỉ để xem, ai có link đều xem được — gỡ link bất cứ lúc nào.",
  },
  journey: { after: ["READ-FUND-MONEY"] },
  help: { title: "Link xem sổ công khai", tryNow: "chia sẻ sổ minh bạch cho cả nhóm" },
} satisfies AiCapabilityPage;
